import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import AdLayout from "../../components/AdLayout";
import "./accountingResponsive.css";
import BankAccountList from "./BankAccountList";
import RevenueCategoryList from "./RevenueCategoryList";
import ExpenseCategoryList from "./ExpenseCategoryList";
import ExpenseObjectList from "./ExpenseObjectList";
import RevenueObjectList from "./RevenueObjectList";
import SalaryConfigList from "./SalaryConfigList";
import axiosInstance from "../../axiosInstance";
import {
  fetchAccountingPermissions,
  ACCOUNTING_CONFIG_BANK,
  ACCOUNTING_CONFIG_REVENUE_CAT,
  ACCOUNTING_CONFIG_EXPENSE_CAT,
  ACCOUNTING_CONFIG_EXPENSE_OBJECT,
  ACCOUNTING_CONFIG_REVENUE_OBJECT,
  ACCOUNTING_CONFIG_SALARY,
} from "../../libs/customerCreatePermission";

const TAB_FEATURES = [
  { key: "bank", title: "Tài khoản ngân hàng", feature: ACCOUNTING_CONFIG_BANK },
  { key: "revenue-cat", title: "Danh mục thu", feature: ACCOUNTING_CONFIG_REVENUE_CAT },
  { key: "expense-cat", title: "Danh mục chi", feature: ACCOUNTING_CONFIG_EXPENSE_CAT },
  { key: "expense-object", title: "Đối tượng chi", feature: ACCOUNTING_CONFIG_EXPENSE_OBJECT },
  { key: "revenue-object", title: "Đối tượng thu", feature: ACCOUNTING_CONFIG_REVENUE_OBJECT },
  { key: "salary-config", title: "Cấu hình lương", feature: ACCOUNTING_CONFIG_SALARY },
];

const AccountingConfig = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "bank";
  const [tabView, setTabView] = useState({});
  const [permLoaded, setPermLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        TAB_FEATURES.map((t) => fetchAccountingPermissions(axiosInstance, t.feature))
      );
      if (cancelled) return;
      const next = {};
      TAB_FEATURES.forEach((t, i) => {
        next[t.key] = Boolean(results[i]?.canView);
      });
      setTabView(next);
      setPermLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const allowedTabs = useMemo(
    () => TAB_FEATURES.filter((t) => tabView[t.key]).map((t) => t.key),
    [tabView]
  );

  useEffect(() => {
    if (!permLoaded || allowedTabs.length === 0) return;
    if (!allowedTabs.includes(tab)) {
      setSearchParams({ tab: allowedTabs[0] }, { replace: true });
    }
  }, [permLoaded, allowedTabs, tab, setSearchParams]);

  const handleTabSelect = (key) => {
    if (!key || !allowedTabs.includes(key)) return;
    setSearchParams({ tab: key });
  };

  if (!permLoaded) return null;

  return (
    <AdLayout>
      <div className="acc-page">
        <h5 className="fw-bold mb-3">Cấu hình kế toán</h5>
        <Tabs
          activeKey={allowedTabs.includes(tab) ? tab : allowedTabs[0]}
          onSelect={handleTabSelect}
          className="mb-3 acc-tabs-scroll"
        >
          {tabView.bank && (
            <Tab eventKey="bank" title="Tài khoản ngân hàng">
              {tab === "bank" && <BankAccountList embedded />}
            </Tab>
          )}
          {tabView["revenue-cat"] && (
            <Tab eventKey="revenue-cat" title="Danh mục thu">
              {tab === "revenue-cat" && <RevenueCategoryList embedded />}
            </Tab>
          )}
          {tabView["expense-cat"] && (
            <Tab eventKey="expense-cat" title="Danh mục chi">
              {tab === "expense-cat" && <ExpenseCategoryList embedded />}
            </Tab>
          )}
          {tabView["expense-object"] && (
            <Tab eventKey="expense-object" title="Đối tượng chi">
              {tab === "expense-object" && <ExpenseObjectList embedded />}
            </Tab>
          )}
          {tabView["revenue-object"] && (
            <Tab eventKey="revenue-object" title="Đối tượng thu">
              {tab === "revenue-object" && <RevenueObjectList embedded />}
            </Tab>
          )}
          {tabView["salary-config"] && (
            <Tab eventKey="salary-config" title="Cấu hình lương">
              {tab === "salary-config" && <SalaryConfigList embedded />}
            </Tab>
          )}
        </Tabs>
      </div>
    </AdLayout>
  );
};

export default AccountingConfig;
